import { Button as RACButton } from 'react-aria-components';

import { Icon } from '../../assets/Icon/Icon';
import {
  arrowsColor,
  destructiveMutedDisabledStyles,
  destructiveMutedEnabledStyles,
  destructiveVividDisabledStyles,
  destructiveVividEnabledStyles,
  primaryMutedDisabledStyles,
  primaryMutedEnabledStyles,
  primaryVividDisabledStyles,
  primaryVividEnabledStyles,
  secondaryMutedDisabledStyles,
  secondaryMutedEnabledStyles,
  secondaryVividDisabledStyles,
  secondaryVividEnabledStyles,
} from '../../controls/Button/Button.styles';
import { selectors, transitions } from '../../controls/shared/styles';
import {
  controlArrowsSize,
  controlBorderRadius,
  controlGap,
  controlHeight,
  controlPaddingX,
  controlPaddingY,
  controlVars,
} from '../../controls/shared/vars';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';

export const SelectContainer = styled('div');
SelectContainer.displayName = 'SelectContainer';

export const SelectArrows = styled(Icon, {
  size: controlArrowsSize,
  color: arrowsColor,
});
SelectArrows.displayName = 'SelectArrows';

export const SelectTrigger = styled(RACButton, {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: controlGap,
  heightAll: controlHeight,
  paddingX: controlPaddingX,
  paddingY: controlPaddingY,
  borderRadius: controlBorderRadius,
  opacity: 1,
  truncate: true,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',
  transition: transitions.control,

  // used to identify as select dropdown for autofocus
  '&': {
    '[data-select-dropdown]': 'true',
  },

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    ...controlVars,
    appearance: {
      muted: {},
      vivid: {},
    },
    isDisabled: {
      true: {
        cursor: 'not-allowed',
        opacity: 0.5,
      },
      false: {},
    },
    variant: {
      destructive: {},
      primary: {},
      secondary: {},
    },
    ghost: {
      true: {},
      false: {},
    },
    invalid: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    // Destructive, vivid
    {
      appearance: 'vivid',
      variant: 'destructive',
      isDisabled: false,
      css: destructiveVividEnabledStyles,
    },
    {
      appearance: 'vivid',
      variant: 'destructive',
      isDisabled: true,
      css: destructiveVividDisabledStyles,
    },

    // Destructive, muted
    {
      appearance: 'muted',
      variant: 'destructive',
      isDisabled: false,
      css: destructiveMutedEnabledStyles,
    },
    {
      appearance: 'muted',
      variant: 'destructive',
      isDisabled: true,
      css: destructiveMutedDisabledStyles,
    },

    // Primary, vivid
    {
      appearance: 'vivid',
      variant: 'primary',
      isDisabled: false,
      css: primaryVividEnabledStyles,
    },
    {
      appearance: 'vivid',
      variant: 'primary',
      isDisabled: true,
      css: primaryVividDisabledStyles,
    },

    // Primary, muted
    {
      appearance: 'muted',
      variant: 'primary',
      isDisabled: false,
      css: primaryMutedEnabledStyles,
    },
    {
      appearance: 'muted',
      variant: 'primary',
      isDisabled: true,
      css: primaryMutedDisabledStyles,
    },

    // Secondary, vivid
    {
      appearance: 'vivid',
      variant: 'secondary',
      isDisabled: false,
      css: secondaryVividEnabledStyles,
    },
    {
      appearance: 'vivid',
      variant: 'secondary',
      isDisabled: true,
      css: secondaryVividDisabledStyles,
    },

    // Secondary, muted
    {
      appearance: 'muted',
      variant: 'secondary',
      isDisabled: false,
      css: secondaryMutedEnabledStyles,
    },
    {
      appearance: 'muted',
      variant: 'secondary',
      isDisabled: true,
      css: secondaryMutedDisabledStyles,
    },

    // Invalid
    {
      isDisabled: false,
      invalid: true,
      css: {
        boxShadow: shadows.fieldErrorLight,

        '&:hover:not(:focus)': {
          boxShadow: shadows.fieldErrorLight,
        },

        [darkThemeSelector]: {
          boxShadow: shadows.fieldErrorDark,

          '&:hover:not(:focus)': {
            boxShadow: shadows.fieldErrorDark,
          },
        },
      },
    },

    // Ghost
    {
      ghost: true,
      css: {
        '&[data-ghost="true"]:not(:hover, :focus, :focus-visible, :focus-within)': {
          backgroundColor: colors.transparent,
          boxShadow: colors.transparent,
          outlineColor: colors.transparent,
        },
      },
    },
  ],
});
SelectTrigger.displayName = 'SelectTrigger';
